/**
 * useDesktopPreferences - manages merged icon layout state
 *
 * Derives default preferences from DesktopConfig, merges with
 * user overrides from localStorage/backend, and exposes actions
 * for reordering, moving, and positioning icons.
 */

import type {
  DesktopPreferences,
  IconGroup,
  IconLayoutMode,
  MobileDockPosition,
  MobileGridPosition,
  SaveDesktopPreferencesRequest,
} from "@repo/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  flushPreferencesOnPageHide,
  loadLocalPreferences,
  repairPreferencesOnBackend,
  saveLocalPreferences,
  scheduleSavePreferences,
  setPreferencesGuestMode,
  syncPreferencesFromBackend,
} from "../services/desktopPreferencesSync";
import type {
  DefaultIconPositions,
  DesktopConfig,
  DesktopIconConfig,
  IconLayoutConfig,
} from "../types";
import { generateUUID } from "../utils/uuid";

export interface DesktopIconPosition {
  id: string;
  x: number;
  y: number;
}

const MOBILE_GRID_COLS = 4;
const MOBILE_GRID_ROWS = 5;
const MOBILE_GRID_PER_PAGE = MOBILE_GRID_COLS * MOBILE_GRID_ROWS;
const MAX_MOBILE_DOCK = 4;

/**
 * JSON stringify that serializes object keys in sorted order, so two
 * structurally-equal objects produce byte-identical strings regardless of
 * insertion order. Required because V8 reshuffles keys on delete+reassign,
 * which poisons any downstream byte-equality check.
 */
function stableStringify(val: unknown): string {
  if (val === null || typeof val !== "object") return JSON.stringify(val);
  if (Array.isArray(val)) {
    return `[${val.map(stableStringify).join(",")}]`;
  }
  const obj = val as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
}

export interface UseDesktopPreferencesReturn {
  layoutMode: IconLayoutMode;
  setLayoutMode: (mode: IconLayoutMode) => void;
  dockIcons: string[];
  desktopIcons: DesktopIconPosition[];
  /** All icon IDs in display order (for left/right/free modes) */
  allIconIds: string[];
  /** Icon IDs hidden from the dock */
  hiddenIcons: string[];
  /**
   * User's explicit visible-dock list. `undefined` means "not yet seeded —
   * fall back to viewport-sized slice of dockIcons". Once seeded (via
   * `setDockVisibleIcons`), only these icons appear in the visible dock;
   * everything else in dockOrder lives in Takeoff / overflow.
   */
  dockVisibleIcons: string[] | undefined;
  /** Replace the explicit visible-dock list wholesale. */
  setDockVisibleIcons: (ids: string[]) => void;
  /** Up to 4 icon IDs pinned to the mobile dock */
  mobileDockIcons: string[];
  isIconInDock: (iconId: string) => boolean;
  isIconOnDesktop: (iconId: string) => boolean;
  isIconHidden: (iconId: string) => boolean;
  isIconInMobileDock: (iconId: string) => boolean;
  moveIconToDock: (iconId: string, insertAtIndex?: number) => void;
  moveIconToDesktop: (
    iconId: string,
    xPercent: number,
    yPercent: number
  ) => void;
  hideIcon: (iconId: string) => void;
  unhideIcon: (iconId: string) => void;
  reorderDock: (fromIndex: number, toIndex: number) => void;
  reorderDockById: (iconId: string, targetIndex: number) => void;
  updateDesktopIconPosition: (
    iconId: string,
    xPercent: number,
    yPercent: number
  ) => void;
  /** Add an icon to the mobile dock (max 4). Returns false if dock is full. */
  addToMobileDock: (iconId: string) => boolean;
  /** Remove an icon from the mobile dock */
  removeFromMobileDock: (iconId: string) => void;
  /** Reorder icons within the mobile dock */
  reorderMobileDock: (fromIndex: number, toIndex: number) => void;
  /** Ordered icon IDs for the mobile grid (excludes dock icons) */
  mobileGridOrder: string[];
  /** Set the full mobile grid order */
  reorderMobileGrid: (newOrder: string[]) => void;
  /** Sparse mobile grid positions keyed by icon ID */
  mobileGridPositions: Record<string, MobileGridPosition>;
  /** Sparse mobile dock positions keyed by icon ID */
  mobileDockPositions: Record<string, MobileDockPosition>;
  /** Place an icon at a specific grid cell (swap if occupied, place if empty) */
  placeIconInGrid: (
    iconId: string,
    page: number,
    row: number,
    col: number
  ) => void;
  /** Place an icon in a specific dock slot (swap if occupied) */
  placeIconInDock: (iconId: string, col: number) => void;
  /** Remove an icon from the mobile grid (sparse) */
  removeFromMobileGrid: (iconId: string) => void;
  /** Place an icon at the first available grid cell */
  addToHomeScreen: (iconId: string) => void;
  hideMenuBarInFullScreen: boolean;
  setHideMenuBarInFullScreen: (v: boolean) => void;
  backgroundId: string | undefined;
  setBackgroundId: (id: string) => void;
  customPexelsIds: string[];
  addCustomPexelsId: (id: string) => void;
  removeCustomPexelsId: (id: string) => void;
  shikiTheme: string | undefined;
  setShikiTheme: (id: string) => void;
  codeEditorFormatOnSave: boolean;
  setCodeEditorFormatOnSave: (v: boolean) => void;
  resetToDefaults: () => void;
  /** Reset icon layout only — dock, positions, mobile grid, hidden, groups. Preserves background/menu/pets/Pexels prefs. */
  /**
   * Reset icon layout to config defaults, scoped to one surface only.
   * "desktop" resets dockOrder / dockVisibleIcons / iconPlacements /
   * hiddenIcons / layoutMode. "mobile" resets mobileGridPositions /
   * mobileDockPositions / iconGroups (+ their legacy derivations). The
   * untouched surface keeps its user-set layout.
   */
  resetIconPositions: (scope: "desktop" | "mobile") => void;
  /**
   * True when the current mobile grid/dock/groups match config defaults —
   * used to disable the "Reset Icon Positions" button on mobile settings
   * after a reset (and re-enable it as soon as the user moves anything).
   */
  isMobileLayoutAtDefaults: boolean;
  /** Same idea, scoped to the desktop dock/placements/hidden list/layoutMode. */
  isDesktopLayoutAtDefaults: boolean;
  isLoading: boolean;
  /** All icon groups (folders) */
  iconGroups: Record<string, IconGroup>;
  /** Create a new group from two icons. Returns the group ID. */
  createGroup: (
    iconIdA: string,
    iconIdB: string,
    position:
      | { type: "grid"; page: number; row: number; col: number }
      | { type: "dock"; col: number }
  ) => string;
  /** Dissolve a group, placing its icons back to available cells */
  dissolveGroup: (groupId: string) => void;
  /** Add an icon to an existing group */
  addToGroup: (groupId: string, iconId: string) => void;
  /** Remove an icon from a group. Auto-dissolves if 1 icon left. */
  removeFromGroup: (groupId: string, iconId: string) => void;
  /** Rename a group */
  renameGroup: (groupId: string, name: string) => void;
  /** Reorder icons within a group */
  reorderGroup: (groupId: string, newOrder: string[]) => void;
  /** Check if an ID is a group */
  isGroup: (id: string) => boolean;
  /** Get a group by ID */
  getGroup: (id: string) => IconGroup | undefined;
}

/**
 * Derive default layout mode from config
 */
function getDefaultLayoutMode(iconLayout?: IconLayoutConfig): IconLayoutMode {
  if (!iconLayout || iconLayout.startPosition === "dock") return "dock";
  if (
    iconLayout.startPosition === "top-left" ||
    iconLayout.startPosition === "bottom-left"
  )
    return "left";
  if (
    iconLayout.startPosition === "top-right" ||
    iconLayout.startPosition === "bottom-right"
  )
    return "right";
  return "dock";
}

/**
 * Build default sparse grid positions from a flat list of icon IDs
 */
function buildDefaultGridPositions(
  iconIds: string[]
): Record<string, MobileGridPosition> {
  const positions: Record<string, MobileGridPosition> = {};
  iconIds.forEach((id, i) => {
    const page = Math.floor(i / MOBILE_GRID_PER_PAGE);
    const posInPage = i % MOBILE_GRID_PER_PAGE;
    positions[id] = {
      page,
      row: Math.floor(posInPage / MOBILE_GRID_COLS),
      col: posInPage % MOBILE_GRID_COLS,
    };
  });
  return positions;
}

/**
 * Build default sparse dock positions from a flat list of icon IDs (max 4)
 */
function buildDefaultDockPositions(
  iconIds: string[]
): Record<string, MobileDockPosition> {
  const positions: Record<string, MobileDockPosition> = {};
  iconIds.slice(0, MAX_MOBILE_DOCK).forEach((id, i) => {
    positions[id] = { col: i };
  });
  return positions;
}

/**
 * Convert a grid position to a linear index for ordering
 */
function posToLinear(pos: MobileGridPosition): number {
  return pos.page * MOBILE_GRID_PER_PAGE + pos.row * MOBILE_GRID_COLS + pos.col;
}

/**
 * Convert a linear index back to a grid position
 */
function linearToPos(index: number): MobileGridPosition {
  const page = Math.floor(index / MOBILE_GRID_PER_PAGE);
  const remainder = index % MOBILE_GRID_PER_PAGE;
  return {
    page,
    row: Math.floor(remainder / MOBILE_GRID_COLS),
    col: remainder % MOBILE_GRID_COLS,
  };
}

/**
 * Find the first empty cell in a sparse grid, starting from a given position
 */
function findFirstEmptyCell(
  positions: Record<string, MobileGridPosition>,
  startPage = 0
): MobileGridPosition {
  // Build a set of occupied cells
  const occupied = new Set<string>();
  for (const pos of Object.values(positions)) {
    occupied.add(`${pos.page}:${pos.row}:${pos.col}`);
  }

  // Find max page
  const maxPage = Math.max(
    startPage,
    ...Object.values(positions).map((p) => p.page)
  );

  // Scan from startPage through maxPage+1 (to create a new page if needed)
  for (let page = startPage; page <= maxPage + 1; page++) {
    for (let row = 0; row < MOBILE_GRID_ROWS; row++) {
      for (let col = 0; col < MOBILE_GRID_COLS; col++) {
        if (!occupied.has(`${page}:${row}:${col}`)) {
          return { page, row, col };
        }
      }
    }
  }

  // Fallback (shouldn't happen)
  return { page: maxPage + 1, row: 0, col: 0 };
}

/**
 * Append to the end of the highest-numbered page. Used when a new app is
 * added to config — we park it after the user's existing layout rather than
 * filling an earlier gap, so their mental model of the grid is preserved.
 */
function findAppendCellOnLastPage(
  positions: Record<string, MobileGridPosition>
): MobileGridPosition {
  const values = Object.values(positions);
  if (values.length === 0) return { page: 0, row: 0, col: 0 };

  const maxPage = Math.max(...values.map((p) => p.page));
  const occupied = new Set<number>();
  for (const p of values) {
    if (p.page === maxPage) {
      occupied.add(p.row * MOBILE_GRID_COLS + p.col);
    }
  }
  for (let i = 0; i < MOBILE_GRID_PER_PAGE; i++) {
    if (!occupied.has(i)) {
      return {
        page: maxPage,
        row: Math.floor(i / MOBILE_GRID_COLS),
        col: i % MOBILE_GRID_COLS,
      };
    }
  }
  // Last page full — spill to a new one.
  return { page: maxPage + 1, row: 0, col: 0 };
}

/**
 * iOS-style displacement: insert an icon at a target cell, shifting
 * all icons from that cell onward forward by one position.
 *
 * Only shifts the consecutive chain of occupied cells starting at the
 * target — preserves gaps elsewhere in the grid.
 */
function displaceIconsAt(
  positions: Record<string, MobileGridPosition>,
  targetPage: number,
  targetRow: number,
  targetCol: number,
  excludeId?: string
): Record<string, MobileGridPosition> {
  const result = { ...positions };
  const targetLinear = posToLinear({
    page: targetPage,
    row: targetRow,
    col: targetCol,
  });

  // Build a map of linear index -> icon ID for quick lookup
  const linearToId = new Map<number, string>();
  for (const [id, pos] of Object.entries(result)) {
    if (id !== excludeId) {
      linearToId.set(posToLinear(pos), id);
    }
  }

  // Find the consecutive chain of occupied cells starting at targetLinear
  // and shift each forward by one position
  const toShift: string[] = [];
  let scanIdx = targetLinear;
  while (linearToId.has(scanIdx)) {
    toShift.push(linearToId.get(scanIdx)!);
    scanIdx++;
  }

  // Shift each icon in the chain forward by one (in reverse to avoid collisions)
  for (let i = toShift.length - 1; i >= 0; i--) {
    const id = toShift[i];
    const oldLinear = posToLinear(result[id]);
    result[id] = linearToPos(oldLinear + 1);
  }

  return result;
}

/**
 * iOS-style displacement for dock: insert an icon at a target column,
 * shifting occupied slots to the right. Icons pushed past column 3
 * overflow into the grid.
 */
function displaceDockIconsAt(
  dockPositions: Record<string, MobileDockPosition>,
  gridPositions: Record<string, MobileGridPosition>,
  targetCol: number,
  excludeId?: string
): {
  dock: Record<string, MobileDockPosition>;
  grid: Record<string, MobileGridPosition>;
} {
  const newDock = { ...dockPositions };
  const newGrid = { ...gridPositions };

  // Build a map of col -> icon ID
  const colToId = new Map<number, string>();
  for (const [id, pos] of Object.entries(newDock)) {
    if (id !== excludeId) {
      colToId.set(pos.col, id);
    }
  }

  // Find consecutive chain of occupied dock slots from targetCol onward
  const toShift: string[] = [];
  let scanCol = targetCol;
  while (scanCol < MAX_MOBILE_DOCK && colToId.has(scanCol)) {
    toShift.push(colToId.get(scanCol)!);
    scanCol++;
  }

  // Shift each in reverse
  for (let i = toShift.length - 1; i >= 0; i--) {
    const id = toShift[i];
    const newCol = newDock[id].col + 1;
    if (newCol >= MAX_MOBILE_DOCK) {
      // Overflow: push to first empty grid cell
      delete newDock[id];
      newGrid[id] = findFirstEmptyCell(newGrid);
    } else {
      newDock[id] = { col: newCol };
    }
  }

  return { dock: newDock, grid: newGrid };
}

/**
 * Remove empty pages and renumber remaining pages sequentially
 */
function compactGridPositions(
  positions: Record<string, MobileGridPosition>
): Record<string, MobileGridPosition> {
  // Find which pages have icons
  const pagesWithIcons = new Set<number>();
  for (const pos of Object.values(positions)) {
    pagesWithIcons.add(pos.page);
  }

  if (pagesWithIcons.size === 0) return positions;

  // Build page renumbering map (only occupied pages, in order)
  const sortedPages = [...pagesWithIcons].sort((a, b) => a - b);
  const pageMap = new Map<number, number>();
  sortedPages.forEach((oldPage, newPage) => {
    pageMap.set(oldPage, newPage);
  });

  // Check if any renumbering is needed
  let needsRenumber = false;
  for (const [oldPage, newPage] of pageMap) {
    if (oldPage !== newPage) {
      needsRenumber = true;
      break;
    }
  }
  if (!needsRenumber) return positions;

  // Apply renumbering
  const result: Record<string, MobileGridPosition> = {};
  for (const [id, pos] of Object.entries(positions)) {
    result[id] = { ...pos, page: pageMap.get(pos.page) ?? pos.page };
  }
  return result;
}

/**
 * Migrate legacy flat arrays to sparse positions
 */
function migrateToSparsePositions(
  prefs: DesktopPreferences
): DesktopPreferences {
  // Already migrated
  if (
    prefs.mobileGridPositions &&
    Object.keys(prefs.mobileGridPositions).length > 0
  ) {
    return prefs;
  }

  const gridOrder = prefs.mobileGridOrder ?? [];
  const dockIcons = prefs.mobileDockIcons ?? [];

  const mobileGridPositions = buildDefaultGridPositions(gridOrder);
  const mobileDockPositions = buildDefaultDockPositions(dockIcons);

  return {
    ...prefs,
    mobileGridPositions,
    mobileDockPositions,
  };
}

/**
 * Validate an author-specified DefaultIconPositions against the icon config.
 *
 * Throws on real authoring bugs (duplicate ids, pinned-id collisions, invalid
 * coords, cell collisions, oversized dock). Unknown ids — i.e. an icon the
 * author named that isn't currently in the icons list — are downgraded to a
 * console.warn and silently dropped by buildDefaults. Icon visibility is
 * dynamic at runtime (auth roles, AppStore install state, feature flags), so
 * "unknown" is not a fail-fast condition — the layout author can't be expected
 * to enumerate every visibility permutation.
 */
function validateDefaultIconPositions(
  icons: DesktopIconConfig[],
  defaults: DefaultIconPositions
): void {
  const iconIds = new Set(icons.map((i) => i.id));
  const pinnedIds = new Set(icons.filter((i) => i.pinned).map((i) => i.id));

  const assert = (cond: boolean, msg: string): void => {
    if (!cond) {
      throw new Error(`[defaultIconPositions] ${msg}`);
    }
  };
  const warnUnknown = (where: string, id: string): void => {
    console.warn(
      `[defaultIconPositions] unknown icon id "${id}" in ${where} — skipping (icon may be filtered by visibility/install state)`
    );
  };

  // Desktop.dockOrder
  if (defaults.desktop) {
    const { dockOrder } = defaults.desktop;
    const seenDesktop = new Set<string>();
    for (const id of dockOrder) {
      if (!iconIds.has(id)) {
        warnUnknown("desktop.dockOrder", id);
        continue;
      }
      assert(
        !pinnedIds.has(id),
        `pinned icon "${id}" cannot be placed in desktop.dockOrder (pinned icons render via their own channel)`
      );
      assert(
        !seenDesktop.has(id),
        `duplicate icon id "${id}" in desktop.dockOrder`
      );
      seenDesktop.add(id);
    }
  }

  // Mobile.dock + Mobile.grids
  if (defaults.mobile) {
    const { dock, grids } = defaults.mobile;
    assert(
      dock.length <= MAX_MOBILE_DOCK,
      `mobile.dock has ${dock.length} entries; max is ${MAX_MOBILE_DOCK}`
    );

    const seenMobile = new Set<string>();
    for (const [idx, id] of dock.entries()) {
      if (id === null) continue;
      if (!iconIds.has(id)) {
        warnUnknown(`mobile.dock[${idx}]`, id);
        continue;
      }
      assert(
        !pinnedIds.has(id),
        `pinned icon "${id}" cannot be placed in mobile.dock`
      );
      assert(!seenMobile.has(id), `duplicate icon id "${id}" in mobile.dock`);
      seenMobile.add(id);
    }

    const cellKeys = new Set<string>();
    const pagesSeen = new Set<number>();
    for (const [id, pos] of Object.entries(grids)) {
      if (!iconIds.has(id)) {
        warnUnknown("mobile.grids", id);
        continue;
      }
      assert(
        !pinnedIds.has(id),
        `pinned icon "${id}" cannot be placed in mobile.grids`
      );
      assert(
        !seenMobile.has(id),
        `icon "${id}" is already in mobile.dock; it cannot also appear in mobile.grids`
      );
      seenMobile.add(id);

      assert(
        Number.isInteger(pos.page) && pos.page >= 0,
        `mobile.grids["${id}"].page must be a non-negative integer (got ${pos.page})`
      );
      assert(
        Number.isInteger(pos.row) && pos.row >= 0 && pos.row < MOBILE_GRID_ROWS,
        `mobile.grids["${id}"].row must be in [0, ${MOBILE_GRID_ROWS - 1}] (got ${pos.row})`
      );
      assert(
        Number.isInteger(pos.col) && pos.col >= 0 && pos.col < MOBILE_GRID_COLS,
        `mobile.grids["${id}"].col must be in [0, ${MOBILE_GRID_COLS - 1}] (got ${pos.col})`
      );

      const cellKey = `${pos.page}:${pos.row}:${pos.col}`;
      assert(
        !cellKeys.has(cellKey),
        `two icons share mobile.grids cell {page:${pos.page}, row:${pos.row}, col:${pos.col}}`
      );
      cellKeys.add(cellKey);
      pagesSeen.add(pos.page);
    }

    if (pagesSeen.size > 0) {
      const maxPage = Math.max(...pagesSeen);
      for (let p = 0; p <= maxPage; p++) {
        assert(
          pagesSeen.has(p),
          `mobile.grids pages must be 0-indexed and sequential with at least one icon per page; page ${p} is missing (max page: ${maxPage})`
        );
      }
    }
  }
}

/**
 * Build default preferences from config. When `customDefaults` is provided
 * (and non-undefined), it is validated and used to drive the initial layout;
 * anything not referenced becomes hidden (desktop) or app-library-only
 * (mobile). When omitted, falls back to the auto-derivation.
 */
function buildDefaults(
  icons: DesktopIconConfig[],
  iconLayout?: IconLayoutConfig,
  customDefaults?: DefaultIconPositions
): DesktopPreferences {
  const layoutMode = getDefaultLayoutMode(iconLayout);
  // Exclude pinned icons from the reorderable dock order.
  // Sort by dockPriority descending so higher-priority icons fill the dock first.
  const nonPinned = icons.filter((i) => !i.pinned);
  const sorted = [...nonPinned].sort(
    (a, b) => (b.dockPriority ?? 0) - (a.dockPriority ?? 0)
  );
  const knownIds = new Set(icons.map((i) => i.id));
  const pinnedIds = new Set(icons.filter((i) => i.pinned).map((i) => i.id));

  if (customDefaults) {
    validateDefaultIconPositions(icons, customDefaults);
  }

  // ---- Desktop side ----
  // Strict: anything non-pinned and not in dockOrder is hidden — no auto-fill.
  // Unknown ids (icon currently filtered out by visibility/install state) are
  // dropped silently; the validator already warned.
  let dockOrder: string[];
  let hiddenIcons: string[] = [];
  const iconPlacements: Record<
    string,
    { location: "dock" | "desktop"; x?: number; y?: number }
  > = {};

  if (customDefaults?.desktop) {
    dockOrder = customDefaults.desktop.dockOrder.filter((id) =>
      knownIds.has(id)
    );
    const inDock = new Set(dockOrder);
    for (const id of dockOrder) {
      iconPlacements[id] = { location: "dock" };
    }
    hiddenIcons = nonPinned.filter((i) => !inDock.has(i.id)).map((i) => i.id);
  } else {
    dockOrder = sorted.map((i) => i.id);
    for (const icon of icons) {
      iconPlacements[icon.id] = { location: "dock" };
    }
  }

  // ---- Mobile side ----
  // Listed icons land exactly where the author put them. Unlisted non-pinned
  // icons are appended to the end of the last home-screen page (in the same
  // dockPriority order the auto-derivation would use), spilling to a new page
  // when the last is full. This way new apps added to config don't disappear
  // on mobile, but the author's hand-tuned layout stays intact.
  let mobileDockIconIds: string[];
  let mobileDockPositions: Record<string, MobileDockPosition>;
  let mobileGridPositions: Record<string, MobileGridPosition>;

  if (customDefaults?.mobile) {
    const { dock, grids } = customDefaults.mobile;
    mobileDockIconIds = [];
    mobileDockPositions = {};
    dock.forEach((id, col) => {
      if (id === null || !knownIds.has(id)) return;
      mobileDockIconIds.push(id);
      mobileDockPositions[id] = { col };
    });
    mobileGridPositions = {};
    for (const [id, pos] of Object.entries(grids)) {
      if (knownIds.has(id)) mobileGridPositions[id] = pos;
    }

    // Auto-append everything else.
    const placedOnMobile = new Set<string>([
      ...mobileDockIconIds,
      ...Object.keys(mobileGridPositions),
    ]);
    for (const icon of sorted) {
      if (placedOnMobile.has(icon.id) || pinnedIds.has(icon.id)) continue;
      mobileGridPositions[icon.id] =
        findAppendCellOnLastPage(mobileGridPositions);
    }
  } else {
    mobileDockIconIds = sorted.slice(0, 4).map((i) => i.id);
    const mobileDockSet = new Set(mobileDockIconIds);
    const gridIconIds = sorted
      .filter((i) => !mobileDockSet.has(i.id))
      .map((i) => i.id);
    mobileGridPositions = buildDefaultGridPositions(gridIconIds);
    mobileDockPositions = buildDefaultDockPositions(mobileDockIconIds);
  }

  // Derive mobileGridOrder from whichever grid positions we ended up with.
  const mobileGridOrder = Object.entries(mobileGridPositions)
    .sort(([, a], [, b]) => {
      if (a.page !== b.page) return a.page - b.page;
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    })
    .map(([id]) => id);

  return {
    layoutMode,
    dockOrder,
    iconPlacements,
    hiddenIcons,
    mobileDockIcons: mobileDockIconIds,
    mobileGridOrder,
    mobileGridPositions,
    mobileDockPositions,
    // Defaults are explicitly NOT stamped with the current time. A
    // freshly-built default snapshot must never look "newer" than valid
    // remote state — otherwise a transient empty/404 fetch can let the
    // defaults win the merge and get pushed back to the server as
    // authoritative, permanently resetting the user's dock. Epoch zero
    // means defaults always lose any timestamp compare.
    updatedAt: "1970-01-01T00:00:00.000Z",
  };
}

/**
 * Assign default grid positions for icons on desktop
 */
function assignDefaultDesktopPositions(
  iconIds: string[],
  startIndex = 0
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  const COLS = 6;
  const X_START = 5;
  const Y_START = 8;
  const X_STEP = 8;
  const Y_STEP = 12;

  iconIds.forEach((id, i) => {
    const idx = startIndex + i;
    const col = idx % COLS;
    const row = Math.floor(idx / COLS);
    positions[id] = {
      x: X_START + col * X_STEP,
      y: Y_START + row * Y_STEP,
    };
  });

  return positions;
}

/**
 * Derive legacy flat arrays from sparse positions (for backward compat)
 */
function deriveLegacyArrays(
  gridPositions: Record<string, MobileGridPosition>,
  dockPositions: Record<string, MobileDockPosition>
): { mobileGridOrder: string[]; mobileDockIcons: string[] } {
  const mobileGridOrder = Object.entries(gridPositions)
    .sort(([, a], [, b]) => {
      if (a.page !== b.page) return a.page - b.page;
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    })
    .map(([id]) => id);

  const mobileDockIcons = Object.entries(dockPositions)
    .sort(([, a], [, b]) => a.col - b.col)
    .map(([id]) => id);

  return { mobileGridOrder, mobileDockIcons };
}

/**
 * Cheap structural compare of the fields that sanitization can mutate.
 * Used to decide whether a freshly-merged snapshot should be pushed back to
 * the backend to overwrite a corrupted row.
 */
function hasMeaningfulDiff(
  remote: DesktopPreferences,
  cleaned: DesktopPreferences
): boolean {
  const fields: (keyof DesktopPreferences)[] = [
    "dockOrder",
    "dockVisibleIcons",
    "hiddenIcons",
    "iconPlacements",
    "mobileDockIcons",
    "mobileGridOrder",
    "mobileGridPositions",
    "mobileDockPositions",
    "iconGroups",
  ];
  for (const f of fields) {
    if (
      JSON.stringify(remote[f] ?? null) !== JSON.stringify(cleaned[f] ?? null)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Merge saved preferences with current config icons.
 *
 * Sanitization invariants enforced here:
 *   - An icon in `hiddenIcons` must NOT appear in any other list.
 *   - An icon inside an `iconGroup` must NOT appear outside that group.
 *   - Pinned icons never appear in `dockOrder` / grid / mobile dock.
 *   - Stale icons (no longer in config) are removed everywhere.
 *   - New icons from config are added to `hiddenIcons` (not the dock), so the
 *     user never gets surprise icons ambushing their layout.
 */
function mergeWithConfig(
  saved: DesktopPreferences,
  configIcons: DesktopIconConfig[]
): DesktopPreferences {
  const pinnedIds = new Set(
    configIcons.filter((i) => i.pinned).map((i) => i.id)
  );

  // Only drop pinned icons from groups (pinned icons aren't meant to be
  // grouped). Stale ids (not in current config) are retained — if
  // visibleConfig transiently shrinks due to AppStore filtering, we don't
  // want to tear users' groups apart. Render-time filtering handles the
  // display. Dedupe in case the same id was stored twice.
  const iconGroups = { ...(saved.iconGroups ?? {}) };
  for (const [groupId, group] of Object.entries(iconGroups)) {
    const seen = new Set<string>();
    const cleanedIds = group.iconIds.filter((id) => {
      if (pinnedIds.has(id)) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (cleanedIds.length === 0) {
      delete iconGroups[groupId];
    } else {
      iconGroups[groupId] = { ...group, iconIds: cleanedIds };
    }
  }
  const groupIds = new Set(Object.keys(iconGroups));
  const groupedIconIds = new Set(
    Object.values(iconGroups).flatMap((g) => g.iconIds)
  );

  // Hidden list: dedupe, drop pinned, drop grouped icons. Stale ids are kept
  // (same reasoning as above). An icon can't be both hidden and inside a
  // group — group wins.
  const hiddenIcons = Array.from(
    new Set(
      (saved.hiddenIcons ?? []).filter(
        (id) => !pinnedIds.has(id) && !groupedIconIds.has(id)
      )
    )
  );
  const hiddenSet = new Set(hiddenIcons);

  // An id is forbidden from a placement list if it's grouped (lives inside a
  // group instead) or pinned (rendered elsewhere). Hidden icons are forbidden
  // from the DESKTOP dockOrder/iconPlacements — that's what hide means there
  // — but keep their mobile positions (desktop hide and mobile pin are
  // independent user intents). Stale icons (not in the current config filter)
  // are NOT stripped; this avoids nuking mobile/desktop layouts when
  // visibleConfig transiently shrinks (e.g. AppStore finishes loading with
  // fewer "installed" apps than the user previously placed). Render-time
  // filtering (isKnownId) already handles non-renderable ids.
  // Desktop: groups are a MOBILE-ONLY concept, so group ids are disallowed
  // in desktop dockOrder/iconPlacements entirely. Grouped icons remain as
  // themselves — grouping on mobile must not alter the desktop dock.
  // Mobile: grouped icons render inside the folder, so they're disallowed
  // as standalone placements; group ids are allowed.
  const isDisallowedForDesktop = (id: string): boolean =>
    pinnedIds.has(id) || hiddenSet.has(id) || groupIds.has(id);
  const isDisallowedForMobile = (id: string): boolean =>
    pinnedIds.has(id) || groupedIconIds.has(id);

  // Map each grouped icon id → its owning group id, so when we strip grouped
  // icons out of dockOrder we can slot the group in at the FIRST position its
  // members used to occupy. Without this, groups get pushed to the end and
  // every overflow icon in the saved dockOrder shifts up into the visible
  // dock — which the user sees as "apps auto-filling that I never put there".
  const iconIdToGroupId = new Map<string, string>();
  for (const [groupId, group] of Object.entries(iconGroups)) {
    for (const iconId of group.iconIds) iconIdToGroupId.set(iconId, groupId);
  }

  // Build dockOrder: preserve saved order, strip disallowed, dedupe.
  // Grouped icons stay as themselves; group ids are rejected (mobile-only).
  // Any legacy group ids left in saved.dockOrder from earlier broken merges
  // are dropped here on the next load.
  const seenInDock = new Set<string>();
  const dockOrder: string[] = [];
  for (const id of saved.dockOrder ?? []) {
    if (seenInDock.has(id)) continue;
    if (!isDisallowedForDesktop(id)) {
      dockOrder.push(id);
      seenInDock.add(id);
    }
  }

  // Rebuild iconPlacements: same rules as dockOrder.
  const iconPlacements: Record<
    string,
    { location: "dock" | "desktop"; x?: number; y?: number }
  > = {};
  for (const [id, placement] of Object.entries(saved.iconPlacements ?? {})) {
    if (!isDisallowedForDesktop(id)) {
      iconPlacements[id] = placement;
    }
  }

  // Migrate legacy flat arrays → sparse positions (only affects pre-v2 data)
  const migrated = migrateToSparsePositions({
    ...saved,
    dockOrder,
    iconPlacements,
    hiddenIcons,
  });

  // Sanitize sparse grid/dock positions. Grouped icons get substituted by the
  // group id (preserving the position slot) so folders appear where their
  // first member was. Hidden icons stay — hidden is a desktop-only concept.
  const gridPositions: Record<string, MobileGridPosition> = {};
  for (const [rawId, pos] of Object.entries(
    migrated.mobileGridPositions ?? {}
  )) {
    const id = iconIdToGroupId.get(rawId) ?? rawId;
    if (id in gridPositions) continue; // group already placed at another member's cell
    if (groupIds.has(id) || !isDisallowedForMobile(id)) {
      gridPositions[id] = pos;
    }
  }
  const dockPositions: Record<string, MobileDockPosition> = {};
  for (const [rawId, pos] of Object.entries(
    migrated.mobileDockPositions ?? {}
  )) {
    const id = iconIdToGroupId.get(rawId) ?? rawId;
    if (id in dockPositions) continue;
    if (groupIds.has(id) || !isDisallowedForMobile(id)) {
      dockPositions[id] = pos;
    }
  }

  // Auto-dissolve single-icon groups after stale cleanup (in-place)
  for (const [groupId, group] of Object.entries(iconGroups)) {
    if (group.iconIds.length !== 1) continue;
    const soloId = group.iconIds[0];
    if (gridPositions[groupId]) {
      gridPositions[soloId] = gridPositions[groupId];
      delete gridPositions[groupId];
    } else if (dockPositions[groupId]) {
      dockPositions[soloId] = dockPositions[groupId];
      delete dockPositions[groupId];
    }
    const dockIdx = dockOrder.indexOf(groupId);
    if (dockIdx >= 0) dockOrder[dockIdx] = soloId;
    if (iconPlacements[groupId]) {
      iconPlacements[soloId] = iconPlacements[groupId];
      delete iconPlacements[groupId];
    }
    delete iconGroups[groupId];
    groupIds.delete(groupId);
    groupedIconIds.delete(soloId);
  }

  // If a group has NO position on either desktop or mobile (e.g. legacy data
  // where createGroup ran before sparse positions), park it in the first
  // empty mobile grid cell so it's reachable. We intentionally do NOT push it
  // to dockOrder — shoving it at the end would bump overflow icons into the
  // visible dock slice, which the user correctly reads as "apps I never
  // placed auto-filling my dock". They can drag the folder back into the
  // dock themselves.
  for (const groupId of groupIds) {
    const hasAnyPlacement =
      dockOrder.includes(groupId) ||
      iconPlacements[groupId] !== undefined ||
      groupId in gridPositions ||
      groupId in dockPositions;
    if (!hasAnyPlacement) {
      gridPositions[groupId] = findFirstEmptyCell(gridPositions);
    }
  }

  // Sanitize dockVisibleIcons: must be a subset of dockOrder, dedupe, drop
  // pinned (rendered elsewhere). Group ids can't survive the dockOrder
  // subset check anymore, so legacy leaked group ids get dropped here too.
  let dockVisibleIcons: string[] | undefined;
  if (saved.dockVisibleIcons) {
    const dockOrderSet = new Set(dockOrder);
    const seenVisible = new Set<string>();
    dockVisibleIcons = [];
    for (const id of saved.dockVisibleIcons) {
      if (seenVisible.has(id)) continue;
      if (!dockOrderSet.has(id)) continue;
      if (pinnedIds.has(id)) continue;
      dockVisibleIcons.push(id);
      seenVisible.add(id);
    }
  }

  // New icons added to config that aren't known anywhere:
  //   Desktop: added to hiddenIcons — they show up in Takeoff/search only,
  //            leaving the user's dock/desktop untouched.
  //   Mobile : appended to the END of the last home-screen page so they're
  //            reachable without reshuffling the existing layout. Earlier
  //            gaps are intentionally NOT filled.
  const knownAnywhere = new Set<string>([
    ...dockOrder,
    ...Object.keys(iconPlacements),
    ...Object.keys(gridPositions),
    ...Object.keys(dockPositions),
    ...hiddenIcons,
    ...groupedIconIds,
  ]);
  for (const icon of configIcons) {
    if (icon.pinned) continue;
    if (!knownAnywhere.has(icon.id)) {
      hiddenIcons.push(icon.id);
      hiddenSet.add(icon.id);
      gridPositions[icon.id] = findAppendCellOnLastPage(gridPositions);
    }
  }

  // Compact pages (removes empty pages) and derive legacy arrays
  const compactedGrid = compactGridPositions(gridPositions);
  const legacy = deriveLegacyArrays(compactedGrid, dockPositions);

  return {
    ...migrated,
    dockOrder,
    dockVisibleIcons,
    iconPlacements,
    hiddenIcons,
    iconGroups: Object.keys(iconGroups).length > 0 ? iconGroups : undefined,
    mobileGridPositions: compactedGrid,
    mobileDockPositions: dockPositions,
    mobileDockIcons: legacy.mobileDockIcons,
    mobileGridOrder: legacy.mobileGridOrder,
  };
}

export function useDesktopPreferences(
  config: DesktopConfig
): UseDesktopPreferencesReturn {
  // No auth in this build: a single local identity that persists preferences to
  // localStorage. `isGuest === false && isAuthenticated === false` routes the
  // load effect down its localStorage-only path (no backend fetch).
  const isGuest = false;
  const isAuthenticated = false;

  const defaults = useRef(
    buildDefaults(config.icons, config.iconLayout, config.defaultIconPositions)
  );
  const cachedPrefs = useRef(loadLocalPreferences());
  const hasCachedPrefs = !!(
    cachedPrefs.current && cachedPrefs.current.layoutMode
  );

  // Skip loading state when localStorage already has user prefs — show them
  // immediately and let the backend sync silently in the background.
  const [isLoading, setIsLoading] = useState(!hasCachedPrefs);

  const [prefs, setPrefs] = useState<DesktopPreferences>(() => {
    if (hasCachedPrefs) {
      const cleaned = mergeWithConfig(cachedPrefs.current!, config.icons);
      // If the cached blob was corrupt, overwrite localStorage immediately
      // so the next refresh doesn't re-render the corruption for a frame.
      if (hasMeaningfulDiff(cachedPrefs.current!, cleaned)) {
        saveLocalPreferences(cleaned);
      }
      return cleaned;
    }
    return defaults.current;
  });
  const initialLoadDone = useRef(hasCachedPrefs);

  // Set guest mode on sync service
  useEffect(() => {
    setPreferencesGuestMode(isGuest);
  }, [isGuest]);

  // Load preferences on mount / auth change (NOT config changes — those are
  // handled by the merge effect below to avoid overwriting user-set prefs
  // with stale backend data).
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Only show loading UI when there's no cached data — returning users
      // already have their prefs rendered from localStorage.
      const hasCache = initialLoadDone.current;
      if (!hasCache) {
        setIsLoading(true);
      }

      try {
        if (isGuest) {
          // Guests: use defaults (no persistence across refreshes)
          if (!hasCache) {
            setPrefs(defaults.current);
          }
          initialLoadDone.current = true;
          setIsLoading(false);
          return;
        }

        if (isAuthenticated) {
          // Try loading from backend
          const remote = await syncPreferencesFromBackend();
          if (cancelled) return;

          if (remote && remote.layoutMode) {
            const cleaned = mergeWithConfig(remote, config.icons);
            setPrefs(cleaned);
            // Self-heal: if sanitization changed anything (e.g. ripped hidden
            // icons out of dockOrder/grid, rehoused grouped icons, etc.),
            // push the cleaned snapshot to the backend immediately so we
            // don't keep rendering from a corrupted blob on future loads.
            if (hasMeaningfulDiff(remote, cleaned)) {
              const payload: SaveDesktopPreferencesRequest = {
                layoutMode: cleaned.layoutMode,
                dockOrder: cleaned.dockOrder,
                dockVisibleIcons: cleaned.dockVisibleIcons,
                iconPlacements: cleaned.iconPlacements,
                hiddenIcons: cleaned.hiddenIcons,
                mobileDockIcons: cleaned.mobileDockIcons,
                mobileGridOrder: cleaned.mobileGridOrder,
                mobileGridPositions: cleaned.mobileGridPositions,
                mobileDockPositions: cleaned.mobileDockPositions,
                hideMenuBarInFullScreen: cleaned.hideMenuBarInFullScreen,
                backgroundId: cleaned.backgroundId,
                customPexelsIds: cleaned.customPexelsIds,
                iconGroups: cleaned.iconGroups,
                shikiTheme: cleaned.shikiTheme,
                codeEditorFormatOnSave: cleaned.codeEditorFormatOnSave,
              };
              saveLocalPreferences(cleaned);
              repairPreferencesOnBackend(payload).catch(() => {});
            }
          } else if (!hasCache) {
            // Only fall back to defaults when we don't already have cached prefs
            const local = loadLocalPreferences();
            if (local && local.layoutMode) {
              setPrefs(mergeWithConfig(local, config.icons));
            } else {
              setPrefs(defaults.current);
            }
          }
        } else if (!hasCache) {
          // Not authenticated and not guest - try localStorage
          const local = loadLocalPreferences();
          if (local && local.layoutMode) {
            setPrefs(mergeWithConfig(local, config.icons));
          } else {
            setPrefs(defaults.current);
          }
        }
      } catch (err) {
        console.warn("Failed to load desktop preferences:", err);
        // Fall back to defaults only when no cached prefs are shown
        if (!hasCache) {
          setPrefs(defaults.current);
        }
      }

      initialLoadDone.current = true;
      setIsLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, isAuthenticated]);

  // Re-merge current prefs with config when icons/layout change (e.g., app
  // store loads, role-gated icons appear). This reconciles stale/missing icon
  // data WITHOUT re-fetching from the backend, so user-set preferences are
  // preserved from the in-memory state.
  const configIconsRef = useRef(config.icons);
  useEffect(() => {
    // Skip the very first render — the load effect above handles initial data.
    if (config.icons === configIconsRef.current) return;
    configIconsRef.current = config.icons;

    if (!initialLoadDone.current) return;

    setPrefs((prev) => mergeWithConfig(prev, config.icons));
  }, [config.icons]);

  // Flush any pending debounced save when the tab is hidden or closed.
  // iOS PWAs die mid-fetch on pagehide, so we ship the pending payload via
  // sendBeacon (keepalive fetch fallback) instead of awaiting a normal save.
  useEffect(() => {
    const flush = () => flushPreferencesOnPageHide();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  // Persist on change (skip initial load)
  const persistPrefs = useCallback((newPrefs: DesktopPreferences) => {
    saveLocalPreferences(newPrefs);
    const payload: SaveDesktopPreferencesRequest = {
      layoutMode: newPrefs.layoutMode,
      dockOrder: newPrefs.dockOrder,
      dockVisibleIcons: newPrefs.dockVisibleIcons,
      iconPlacements: newPrefs.iconPlacements,
      hiddenIcons: newPrefs.hiddenIcons,
      mobileDockIcons: newPrefs.mobileDockIcons,
      mobileGridOrder: newPrefs.mobileGridOrder,
      mobileGridPositions: newPrefs.mobileGridPositions,
      mobileDockPositions: newPrefs.mobileDockPositions,
      hideMenuBarInFullScreen: newPrefs.hideMenuBarInFullScreen,
      backgroundId: newPrefs.backgroundId,
      customPexelsIds: newPrefs.customPexelsIds,
      iconGroups: newPrefs.iconGroups,
      shikiTheme: newPrefs.shikiTheme,
      codeEditorFormatOnSave: newPrefs.codeEditorFormatOnSave,
    };
    scheduleSavePreferences(payload);
  }, []);

  const updatePrefs = useCallback(
    (updater: (prev: DesktopPreferences) => DesktopPreferences) => {
      setPrefs((prev) => {
        const next = updater(prev);
        if (initialLoadDone.current) {
          persistPrefs(next);
        }
        return next;
      });
    },
    [persistPrefs]
  );

  const setLayoutMode = useCallback(
    (mode: IconLayoutMode) => {
      updatePrefs((prev) => {
        if (mode === "dock") {
          // Moving to dock: put all non-pinned icons in dock
          const nonPinnedIds = config.icons
            .filter((i) => !i.pinned)
            .map((i) => i.id);
          const newPlacements: Record<
            string,
            { location: "dock" | "desktop" }
          > = {};
          for (const icon of config.icons) {
            newPlacements[icon.id] = { location: "dock" };
          }
          return {
            ...prev,
            layoutMode: mode,
            dockOrder: nonPinnedIds,
            iconPlacements: newPlacements,
            updatedAt: new Date().toISOString(),
          };
        }

        // Moving to left/right/free
        if (mode === "free") {
          // Assign default positions to icons that don't have desktop positions
          const newPlacements = { ...prev.iconPlacements };
          const needsPosition = config.icons.filter(
            (i) =>
              !newPlacements[i.id] || newPlacements[i.id].location !== "desktop"
          );
          const defaultPositions = assignDefaultDesktopPositions(
            needsPosition.map((i) => i.id)
          );
          for (const [id, pos] of Object.entries(defaultPositions)) {
            newPlacements[id] = { location: "desktop", x: pos.x, y: pos.y };
          }
          return {
            ...prev,
            layoutMode: mode,
            iconPlacements: newPlacements,
            updatedAt: new Date().toISOString(),
          };
        }

        // Left/right: just change mode, keep placements
        return {
          ...prev,
          layoutMode: mode,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs, config.icons]
  );

  const setHideMenuBarInFullScreen = useCallback(
    (v: boolean) => {
      updatePrefs((prev) => ({
        ...prev,
        hideMenuBarInFullScreen: v,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePrefs]
  );

  const setBackgroundId = useCallback(
    (id: string) => {
      updatePrefs((prev) => ({
        ...prev,
        backgroundId: id,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePrefs]
  );

  const setShikiTheme = useCallback(
    (id: string) => {
      updatePrefs((prev) => ({
        ...prev,
        shikiTheme: id,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePrefs]
  );

  const setCodeEditorFormatOnSave = useCallback(
    (v: boolean) => {
      updatePrefs((prev) => ({
        ...prev,
        codeEditorFormatOnSave: v,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePrefs]
  );

  const addCustomPexelsId = useCallback(
    (id: string) => {
      updatePrefs((prev) => {
        const existing = prev.customPexelsIds ?? [];
        if (existing.includes(id)) return prev;
        return {
          ...prev,
          customPexelsIds: [...existing, id],
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const removeCustomPexelsId = useCallback(
    (id: string) => {
      updatePrefs((prev) => {
        const existing = prev.customPexelsIds ?? [];
        const backgroundWasCustom = prev.backgroundId === `pexels:${id}`;
        return {
          ...prev,
          customPexelsIds: existing.filter((x) => x !== id),
          // Reset to default if we're removing the active background
          ...(backgroundWasCustom ? { backgroundId: "default" } : {}),
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const isIconInDock = useCallback(
    (iconId: string) => {
      return prefs.dockOrder.includes(iconId);
    },
    [prefs.dockOrder]
  );

  const isIconOnDesktop = useCallback(
    (iconId: string) => {
      return prefs.iconPlacements[iconId]?.location === "desktop";
    },
    [prefs.iconPlacements]
  );

  const pinnedIconIds = new Set(
    config.icons.filter((i) => i.pinned).map((i) => i.id)
  );

  const moveIconToDock = useCallback(
    (iconId: string, insertAtIndex?: number) => {
      if (pinnedIconIds.has(iconId)) return; // pinned icons cannot be moved
      updatePrefs((prev) => {
        // Remove from dock order first (in case it's already there)
        const filtered = prev.dockOrder.filter((id) => id !== iconId);
        let newDockOrder: string[];
        if (insertAtIndex !== undefined && insertAtIndex >= 0) {
          newDockOrder = [...filtered];
          newDockOrder.splice(
            Math.min(insertAtIndex, filtered.length),
            0,
            iconId
          );
        } else {
          newDockOrder = [...filtered, iconId];
        }
        const newPlacements = { ...prev.iconPlacements };
        newPlacements[iconId] = { location: "dock" };
        // Also remove from hidden if it was hidden
        const hidden = (prev.hiddenIcons ?? []).filter((id) => id !== iconId);
        // Dragging an icon INTO the dock means the user wants it visible.
        const prevVisible = prev.dockVisibleIcons;
        const nextVisible =
          prevVisible === undefined
            ? undefined
            : prevVisible.includes(iconId)
              ? prevVisible
              : [...prevVisible, iconId];
        return {
          ...prev,
          dockOrder: newDockOrder,
          dockVisibleIcons: nextVisible,
          iconPlacements: newPlacements,
          hiddenIcons: hidden,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const moveIconToDesktop = useCallback(
    (iconId: string, xPercent: number, yPercent: number) => {
      if (pinnedIconIds.has(iconId)) return; // pinned icons cannot be moved
      updatePrefs((prev) => {
        const newDockOrder = prev.dockOrder.filter((id) => id !== iconId);
        const newPlacements = { ...prev.iconPlacements };
        newPlacements[iconId] = {
          location: "desktop",
          x: xPercent,
          y: yPercent,
        };
        // Also remove from hidden if it was hidden
        const hidden = (prev.hiddenIcons ?? []).filter((id) => id !== iconId);
        const nextVisible = prev.dockVisibleIcons?.filter(
          (id) => id !== iconId
        );
        return {
          ...prev,
          dockVisibleIcons: nextVisible,
          dockOrder: newDockOrder,
          iconPlacements: newPlacements,
          hiddenIcons: hidden,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const reorderDock = useCallback(
    (fromIndex: number, toIndex: number) => {
      updatePrefs((prev) => {
        const newOrder = [...prev.dockOrder];
        const [moved] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, moved);
        return {
          ...prev,
          dockOrder: newOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  /** Move an icon to a specific position in dockOrder by ID (avoids index-mapping issues) */
  const reorderDockById = useCallback(
    (iconId: string, targetIndex: number) => {
      updatePrefs((prev) => {
        const filtered = prev.dockOrder.filter((id) => id !== iconId);
        const clamped = Math.max(0, Math.min(targetIndex, filtered.length));
        const newOrder = [...filtered];
        newOrder.splice(clamped, 0, iconId);
        // Skip update if order didn't change
        if (
          newOrder.every((id, i) => id === prev.dockOrder[i]) &&
          newOrder.length === prev.dockOrder.length
        ) {
          return prev;
        }
        return {
          ...prev,
          dockOrder: newOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const updateDesktopIconPosition = useCallback(
    (iconId: string, xPercent: number, yPercent: number) => {
      updatePrefs((prev) => {
        const newPlacements = { ...prev.iconPlacements };
        newPlacements[iconId] = {
          location: "desktop",
          x: xPercent,
          y: yPercent,
        };
        return {
          ...prev,
          iconPlacements: newPlacements,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const hideIcon = useCallback(
    (iconId: string) => {
      if (pinnedIconIds.has(iconId)) return;
      updatePrefs((prev) => {
        const hidden = prev.hiddenIcons ?? [];
        if (hidden.includes(iconId)) return prev;
        const newDockOrder = prev.dockOrder.filter((id) => id !== iconId);
        const newPlacements = { ...prev.iconPlacements };
        delete newPlacements[iconId];
        const nextVisible = prev.dockVisibleIcons?.filter(
          (id) => id !== iconId
        );
        return {
          ...prev,
          dockOrder: newDockOrder,
          dockVisibleIcons: nextVisible,
          iconPlacements: newPlacements,
          hiddenIcons: [...hidden, iconId],
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const unhideIcon = useCallback(
    (iconId: string) => {
      updatePrefs((prev) => {
        const hidden = (prev.hiddenIcons ?? []).filter((id) => id !== iconId);
        const newDockOrder = prev.dockOrder.includes(iconId)
          ? prev.dockOrder
          : [...prev.dockOrder, iconId];
        const newPlacements = { ...prev.iconPlacements };
        newPlacements[iconId] = { location: "dock" };
        // Unhiding should put the icon back in the visible dock set
        const prevVisible = prev.dockVisibleIcons;
        const nextVisible =
          prevVisible === undefined
            ? undefined
            : prevVisible.includes(iconId)
              ? prevVisible
              : [...prevVisible, iconId];
        return {
          ...prev,
          dockOrder: newDockOrder,
          dockVisibleIcons: nextVisible,
          iconPlacements: newPlacements,
          hiddenIcons: hidden,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const isIconHidden = useCallback(
    (iconId: string) => {
      return (prefs.hiddenIcons ?? []).includes(iconId);
    },
    [prefs.hiddenIcons]
  );

  // Mobile dock actions
  const isIconInMobileDock = useCallback(
    (iconId: string) => {
      const dockPos = prefs.mobileDockPositions ?? {};
      return iconId in dockPos;
    },
    [prefs.mobileDockPositions]
  );

  const addToMobileDock = useCallback(
    (iconId: string): boolean => {
      const currentDock = prefs.mobileDockPositions ?? {};
      if (
        Object.keys(currentDock).length >= MAX_MOBILE_DOCK ||
        iconId in currentDock
      )
        return false;
      // Find first empty dock slot
      const occupied = new Set(Object.values(currentDock).map((p) => p.col));
      let targetCol = -1;
      for (let c = 0; c < MAX_MOBILE_DOCK; c++) {
        if (!occupied.has(c)) {
          targetCol = c;
          break;
        }
      }
      if (targetCol < 0) return false;

      updatePrefs((prev) => {
        const newDockPositions = { ...(prev.mobileDockPositions ?? {}) };
        newDockPositions[iconId] = { col: targetCol };
        const newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        delete newGridPositions[iconId];
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);
        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
      return true;
    },
    [prefs.mobileDockPositions, updatePrefs]
  );

  const removeFromMobileDock = useCallback(
    (iconId: string) => {
      updatePrefs((prev) => {
        const newDockPositions = { ...(prev.mobileDockPositions ?? {}) };
        delete newDockPositions[iconId];
        // Place in first empty grid cell
        const newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        newGridPositions[iconId] = findFirstEmptyCell(newGridPositions);
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);
        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const reorderMobileDock = useCallback(
    (fromIndex: number, toIndex: number) => {
      updatePrefs((prev) => {
        const order = [...(prev.mobileDockIcons ?? [])];
        const [moved] = order.splice(fromIndex, 1);
        order.splice(toIndex, 0, moved);
        const newDockPositions: Record<string, MobileDockPosition> = {};
        order.forEach((id, i) => {
          newDockPositions[id] = { col: i };
        });
        return {
          ...prev,
          mobileDockIcons: order,
          mobileDockPositions: newDockPositions,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const reorderMobileGrid = useCallback(
    (newOrder: string[]) => {
      // Deduplicate while preserving order
      const seen = new Set<string>();
      const deduped = newOrder.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      // Rebuild grid positions from the new order (sequential fill)
      const newGridPositions = buildDefaultGridPositions(deduped);
      updatePrefs((prev) => ({
        ...prev,
        mobileGridOrder: deduped,
        mobileGridPositions: newGridPositions,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePrefs]
  );

  // Sparse grid placement: place icon at specific cell (iOS-style displacement)
  const placeIconInGrid = useCallback(
    (iconId: string, page: number, row: number, col: number) => {
      updatePrefs((prev) => {
        let newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        const newDockPositions = { ...(prev.mobileDockPositions ?? {}) };

        // Remove the dragged icon from its current position first
        delete newGridPositions[iconId];
        delete newDockPositions[iconId];

        // Check if target cell is occupied (by another icon)
        const isOccupied = Object.entries(newGridPositions).some(
          ([, pos]) => pos.page === page && pos.row === row && pos.col === col
        );

        if (isOccupied) {
          // iOS-style: displace icons from target cell onward
          newGridPositions = displaceIconsAt(newGridPositions, page, row, col);
        }

        // Place the icon at target
        newGridPositions[iconId] = { page, row, col };

        // Compact pages
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  // Sparse dock placement: place icon at specific dock slot (iOS-style displacement)
  const placeIconInDock = useCallback(
    (iconId: string, col: number) => {
      updatePrefs((prev) => {
        let newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        let newDockPositions = { ...(prev.mobileDockPositions ?? {}) };

        // Remove the dragged icon from its current position first
        delete newGridPositions[iconId];
        delete newDockPositions[iconId];

        // Check if target dock slot is occupied
        const isOccupied = Object.entries(newDockPositions).some(
          ([, pos]) => pos.col === col
        );

        if (isOccupied) {
          // iOS-style: displace dock icons from target slot onward
          // Icons pushed past slot 3 overflow into the grid
          const displaced = displaceDockIconsAt(
            newDockPositions,
            newGridPositions,
            col
          );
          newDockPositions = displaced.dock;
          newGridPositions = displaced.grid;
        }

        // Place icon in dock
        newDockPositions[iconId] = { col };

        // Compact pages
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  // Remove icon from mobile grid (sparse)
  const removeFromMobileGrid = useCallback(
    (iconId: string) => {
      updatePrefs((prev) => {
        const newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        delete newGridPositions[iconId];
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(
          compacted,
          prev.mobileDockPositions ?? {}
        );
        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const addToHomeScreen = useCallback(
    (iconId: string) => {
      updatePrefs((prev) => {
        const gridPositions = prev.mobileGridPositions ?? {};
        // Already on home screen
        if (gridPositions[iconId]) return prev;
        const cell = findFirstEmptyCell(gridPositions);
        const newGridPositions = { ...gridPositions, [iconId]: cell };
        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(
          compacted,
          prev.mobileDockPositions ?? {}
        );
        return {
          ...prev,
          mobileGridPositions: compacted,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  // ---- Icon Group (Folder) Methods ----

  const iconGroups = prefs.iconGroups ?? {};

  const isGroup = useCallback(
    (id: string) => !!(prefs.iconGroups ?? {})[id],
    [prefs.iconGroups]
  );

  const getGroup = useCallback(
    (id: string) => (prefs.iconGroups ?? {})[id],
    [prefs.iconGroups]
  );

  /**
   * Remove an icon id from the MOBILE position maps only. Desktop state
   * (dockOrder, dockVisibleIcons, iconPlacements) is intentionally untouched
   * — mobile and desktop layouts are fully decoupled, so grouping/ungrouping
   * an icon on mobile must never alter where it lives on desktop.
   */
  function removeIconFromMobilePositionMaps(
    prev: DesktopPreferences,
    iconId: string
  ) {
    const newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
    const newDockPositions = { ...(prev.mobileDockPositions ?? {}) };
    delete newGridPositions[iconId];
    delete newDockPositions[iconId];
    return { newGridPositions, newDockPositions };
  }

  const createGroup = useCallback(
    (
      iconIdA: string,
      iconIdB: string,
      position:
        | { type: "grid"; page: number; row: number; col: number }
        | { type: "dock"; col: number }
    ): string => {
      // Don't group pinned icons
      if (pinnedIconIds.has(iconIdA) || pinnedIconIds.has(iconIdB)) return "";
      const groupId = `group_${generateUUID().slice(0, 8)}`;

      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };

        // Collect icon IDs — if either is already a group, merge its icons
        const idsA = groups[iconIdA] ? [...groups[iconIdA].iconIds] : [iconIdA];
        const idsB = groups[iconIdB] ? [...groups[iconIdB].iconIds] : [iconIdB];

        // Remove old groups if merging
        if (groups[iconIdA]) delete groups[iconIdA];
        if (groups[iconIdB]) delete groups[iconIdB];

        groups[groupId] = {
          id: groupId,
          name: "Folder",
          iconIds: [...idsA, ...idsB],
        };

        // Remove both icons/groups from MOBILE position maps only. Their
        // desktop placements (dockOrder / iconPlacements) are left alone —
        // grouping on mobile is a mobile-only operation.
        let { newGridPositions, newDockPositions } =
          removeIconFromMobilePositionMaps(prev, iconIdA);
        ({ newGridPositions, newDockPositions } =
          removeIconFromMobilePositionMaps(
            {
              ...prev,
              mobileGridPositions: newGridPositions,
              mobileDockPositions: newDockPositions,
            },
            iconIdB
          ));

        // Place the group at the target mobile position. Groups are a
        // mobile-only concept; the "desktop" position branch was never
        // reachable from current UI and has been removed.
        if (position.type === "grid") {
          newGridPositions[groupId] = {
            page: position.page,
            row: position.row,
            col: position.col,
          };
        } else if (position.type === "dock") {
          newDockPositions[groupId] = { col: position.col };
        }

        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          iconGroups: groups,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });

      return groupId;
    },
    [updatePrefs, pinnedIconIds]
  );

  const dissolveGroup = useCallback(
    (groupId: string) => {
      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };
        const group = groups[groupId];
        if (!group) return prev;

        // Remove the group from mobile position maps (groups are mobile-only).
        const { newGridPositions, newDockPositions } =
          removeIconFromMobilePositionMaps(prev, groupId);

        // Scatter the former members into free mobile grid cells.
        for (const iconId of group.iconIds) {
          const cell = findFirstEmptyCell(newGridPositions);
          newGridPositions[iconId] = cell;
        }

        delete groups[groupId];

        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          iconGroups: Object.keys(groups).length > 0 ? groups : undefined,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const addToGroup = useCallback(
    (groupId: string, iconId: string) => {
      if (pinnedIconIds.has(iconId)) return;
      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };
        const group = groups[groupId];
        if (!group) return prev;
        if (group.iconIds.includes(iconId)) return prev;

        // If the icon being added is itself a group, merge its icons
        if (groups[iconId]) {
          groups[groupId] = {
            ...group,
            iconIds: [...group.iconIds, ...groups[iconId].iconIds],
          };
          delete groups[iconId];
        } else {
          groups[groupId] = {
            ...group,
            iconIds: [...group.iconIds, iconId],
          };
        }

        // Remove icon from position maps
        // addToGroup is mobile-only — leave desktop dockOrder/iconPlacements
        // alone. The icon keeps its desktop slot regardless of its mobile
        // folder membership.
        const { newGridPositions, newDockPositions } =
          removeIconFromMobilePositionMaps(prev, iconId);

        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          iconGroups: groups,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs, pinnedIconIds]
  );

  const removeFromGroup = useCallback(
    (groupId: string, iconId: string) => {
      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };
        const group = groups[groupId];
        if (!group) return prev;

        const remaining = group.iconIds.filter((id) => id !== iconId);

        // removeFromGroup is mobile-only — never mutate desktop dockOrder or
        // iconPlacements. Only mobile grid/dock positions + iconGroups change.
        const newGridPositions = { ...(prev.mobileGridPositions ?? {}) };
        const newDockPositions = { ...(prev.mobileDockPositions ?? {}) };

        newGridPositions[iconId] = findFirstEmptyCell(newGridPositions);

        if (remaining.length <= 1) {
          // Auto-dissolve: group has 0-1 icons left
          if (remaining.length === 1) {
            const soloId = remaining[0];
            // Transfer the group's mobile position to the solo icon.
            if (newGridPositions[groupId]) {
              newGridPositions[soloId] = newGridPositions[groupId];
              delete newGridPositions[groupId];
            } else if (newDockPositions[groupId]) {
              newDockPositions[soloId] = newDockPositions[groupId];
              delete newDockPositions[groupId];
            }
          } else {
            // 0 remaining — just drop the group's mobile positions.
            delete newGridPositions[groupId];
            delete newDockPositions[groupId];
          }
          delete groups[groupId];
        } else {
          groups[groupId] = { ...group, iconIds: remaining };
        }

        const compacted = compactGridPositions(newGridPositions);
        const legacy = deriveLegacyArrays(compacted, newDockPositions);

        return {
          ...prev,
          iconGroups: Object.keys(groups).length > 0 ? groups : undefined,
          mobileGridPositions: compacted,
          mobileDockPositions: newDockPositions,
          mobileDockIcons: legacy.mobileDockIcons,
          mobileGridOrder: legacy.mobileGridOrder,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const renameGroup = useCallback(
    (groupId: string, name: string) => {
      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };
        const group = groups[groupId];
        if (!group) return prev;
        groups[groupId] = { ...group, name };
        return {
          ...prev,
          iconGroups: groups,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const reorderGroup = useCallback(
    (groupId: string, newOrder: string[]) => {
      updatePrefs((prev) => {
        const groups = { ...(prev.iconGroups ?? {}) };
        const group = groups[groupId];
        if (!group) return prev;
        groups[groupId] = { ...group, iconIds: newOrder };
        return {
          ...prev,
          iconGroups: groups,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const setDockVisibleIcons = useCallback(
    (ids: string[]) => {
      updatePrefs((prev) => {
        const dockSet = new Set(prev.dockOrder);
        const seen = new Set<string>();
        const cleaned: string[] = [];
        for (const id of ids) {
          if (seen.has(id)) continue;
          if (!dockSet.has(id)) continue;
          cleaned.push(id);
          seen.add(id);
        }
        return {
          ...prev,
          dockVisibleIcons: cleaned,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [updatePrefs]
  );

  const resetToDefaults = useCallback(() => {
    const defs = buildDefaults(
      config.icons,
      config.iconLayout,
      config.defaultIconPositions
    );
    setPrefs(defs);
    // Intentionally DO NOT persist. This is fired on the "desktop:logout"
    // event to reset local UI state — scheduling a backend save here would
    // race with auth teardown and stamp defaults over the logging-out user's
    // real preferences (or, worse, over the next logged-in user's prefs if
    // the debounce flushes after a quick re-login).
  }, [config.icons, config.iconLayout, config.defaultIconPositions]);

  /**
   * Reset icon-layout fields to config defaults, scoped to the surface the
   * user is actually viewing. Preserves non-layout preferences (background,
   * wallpaper, menu-bar behavior, custom Pexels IDs) AND the
   * OTHER surface's layout — resetting mobile keeps the user's desktop dock
   * intact, and vice versa.
   */
  const resetIconPositions = useCallback(
    (scope: "desktop" | "mobile") => {
      const defs = buildDefaults(
        config.icons,
        config.iconLayout,
        config.defaultIconPositions
      );
      updatePrefs((prev) => {
        if (scope === "desktop") {
          return {
            ...prev,
            layoutMode: defs.layoutMode,
            dockOrder: defs.dockOrder,
            dockVisibleIcons: undefined,
            iconPlacements: defs.iconPlacements,
            // Honor author-specified hidden list when custom defaults
            // define desktop.dockOrder; otherwise clear the hidden list.
            hiddenIcons: defs.hiddenIcons ?? [],
            updatedAt: new Date().toISOString(),
          };
        }
        return {
          ...prev,
          mobileDockIcons: defs.mobileDockIcons,
          mobileGridOrder: defs.mobileGridOrder,
          mobileGridPositions: defs.mobileGridPositions,
          mobileDockPositions: defs.mobileDockPositions,
          iconGroups: undefined,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [config.icons, config.iconLayout, config.defaultIconPositions, updatePrefs]
  );

  // Derived: desktop icons with positions
  const desktopIcons: DesktopIconPosition[] = Object.entries(
    prefs.iconPlacements
  )
    .filter(([, placement]) => placement.location === "desktop")
    .map(([id, placement]) => ({
      id,
      x: placement.x ?? 10,
      y: placement.y ?? 10,
    }));

  // All icon IDs in a consistent order (for left/right/free rendering)
  const allIconIds = config.icons.map((i) => i.id);

  // Derived: sparse positions
  const mobileGridPositions = prefs.mobileGridPositions ?? {};
  const mobileDockPositions = prefs.mobileDockPositions ?? {};

  // Derived: mobile dock icons from sparse positions (include group IDs)
  const configIconIds = new Set(config.icons.map((i) => i.id));
  const groupIdSet = useMemo(
    () => new Set(Object.keys(iconGroups)),
    [iconGroups]
  );
  const isKnownId = useCallback(
    (id: string) => configIconIds.has(id) || groupIdSet.has(id),
    [configIconIds, groupIdSet]
  );
  const mobileDockIcons = useMemo(() => {
    return Object.entries(mobileDockPositions)
      .filter(([id]) => isKnownId(id))
      .sort(([, a], [, b]) => a.col - b.col)
      .map(([id]) => id);
  }, [mobileDockPositions, isKnownId]);

  // Derived: mobile grid order from sparse positions (include group IDs)
  const mobileDockSet = new Set(mobileDockIcons);
  const mobileGridOrder = useMemo(() => {
    return Object.entries(mobileGridPositions)
      .filter(([id]) => isKnownId(id) && !mobileDockSet.has(id))
      .sort(([, a], [, b]) => {
        if (a.page !== b.page) return a.page - b.page;
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
      })
      .map(([id]) => id);
  }, [mobileGridPositions, isKnownId, mobileDockSet]);

  // Derived: whether each surface currently matches config defaults. Used to
  // disable the "Reset Icon Positions" button in Settings once a reset has
  // taken effect (and re-enable it as soon as the user moves anything).
  //
  // NOTE: we deliberately use a stable (sorted-key) stringify rather than
  // JSON.stringify. Runtime mutations like `delete + reassign` (see
  // placeIconInGrid) shuffle object key order without changing the data, so
  // a naïve JSON compare produces false negatives where the user IS at
  // defaults but byte-equality fails.
  const isMobileLayoutAtDefaults = useMemo(() => {
    const defs = buildDefaults(
      config.icons,
      config.iconLayout,
      config.defaultIconPositions
    );
    const checks = {
      gridPositions:
        stableStringify(prefs.mobileGridPositions ?? {}) ===
        stableStringify(defs.mobileGridPositions ?? {}),
      dockPositions:
        stableStringify(prefs.mobileDockPositions ?? {}) ===
        stableStringify(defs.mobileDockPositions ?? {}),
      dockIcons:
        stableStringify(prefs.mobileDockIcons ?? []) ===
        stableStringify(defs.mobileDockIcons ?? []),
      gridOrder:
        stableStringify(prefs.mobileGridOrder ?? []) ===
        stableStringify(defs.mobileGridOrder ?? []),
      noGroups: Object.keys(prefs.iconGroups ?? {}).length === 0,
    };
    // eslint-disable-next-line no-console
    return (
      checks.gridPositions &&
      checks.dockPositions &&
      checks.dockIcons &&
      checks.gridOrder &&
      checks.noGroups
    );
  }, [
    config.icons,
    config.iconLayout,
    config.defaultIconPositions,
    prefs.mobileGridPositions,
    prefs.mobileDockPositions,
    prefs.mobileDockIcons,
    prefs.mobileGridOrder,
    prefs.iconGroups,
  ]);

  const isDesktopLayoutAtDefaults = useMemo(() => {
    const defs = buildDefaults(
      config.icons,
      config.iconLayout,
      config.defaultIconPositions
    );
    return (
      prefs.layoutMode === defs.layoutMode &&
      stableStringify(prefs.dockOrder) === stableStringify(defs.dockOrder) &&
      stableStringify(prefs.iconPlacements) ===
        stableStringify(defs.iconPlacements) &&
      stableStringify(prefs.hiddenIcons ?? []) ===
        stableStringify(defs.hiddenIcons ?? []) &&
      prefs.dockVisibleIcons === undefined
    );
  }, [
    config.icons,
    config.iconLayout,
    config.defaultIconPositions,
    prefs.layoutMode,
    prefs.dockOrder,
    prefs.iconPlacements,
    prefs.hiddenIcons,
    prefs.dockVisibleIcons,
  ]);

  return {
    layoutMode: prefs.layoutMode,
    setLayoutMode,
    dockIcons: prefs.dockOrder,
    desktopIcons,
    allIconIds,
    hiddenIcons: prefs.hiddenIcons ?? [],
    dockVisibleIcons: prefs.dockVisibleIcons,
    setDockVisibleIcons,
    mobileDockIcons,
    isIconInDock,
    isIconOnDesktop,
    isIconHidden,
    isIconInMobileDock,
    moveIconToDock,
    moveIconToDesktop,
    hideIcon,
    unhideIcon,
    reorderDock,
    reorderDockById,
    updateDesktopIconPosition,
    addToMobileDock,
    removeFromMobileDock,
    reorderMobileDock,
    mobileGridOrder,
    reorderMobileGrid,
    mobileGridPositions,
    mobileDockPositions,
    placeIconInGrid,
    placeIconInDock,
    removeFromMobileGrid,
    addToHomeScreen,
    hideMenuBarInFullScreen: prefs.hideMenuBarInFullScreen ?? true,
    setHideMenuBarInFullScreen,
    backgroundId: prefs.backgroundId,
    setBackgroundId,
    customPexelsIds: prefs.customPexelsIds ?? [],
    addCustomPexelsId,
    removeCustomPexelsId,
    shikiTheme: prefs.shikiTheme,
    setShikiTheme,
    codeEditorFormatOnSave: prefs.codeEditorFormatOnSave ?? false,
    setCodeEditorFormatOnSave,
    resetToDefaults,
    resetIconPositions,
    isMobileLayoutAtDefaults,
    isDesktopLayoutAtDefaults,
    isLoading,
    iconGroups,
    createGroup,
    dissolveGroup,
    addToGroup,
    removeFromGroup,
    renameGroup,
    reorderGroup,
    isGroup,
    getGroup,
  };
}
